/**
 * Уровни просмотра: район → массив → хозяйство → контур → работа.
 *
 * Зачем этот модуль вообще есть. Экраны выросли по одному, и каждый решал сам,
 * какую статистику показать и что раскрыть по нажатию. В итоге на первом экране
 * района оказались 39 категорий землепользования, 22 массива, 8 слоёв
 * готовности и 11 видов событий — всё разом и всё одинаковым весом. Это не
 * «много данных», это отсутствие объявленного уровня: у каждой величины есть
 * знаменатель, и показывать её надо там, где этот знаменатель — предмет
 * разговора.
 *
 * Здесь объявлено ОДНО правило на весь продукт:
 *
 *   1. **У уровня есть единица счёта.** Район считается массивами, массив —
 *      контурами и хозяйствами, хозяйство — контурами и обязательствами,
 *      контур — работами, работа — отчётом и проверкой. Доля, посчитанная не в
 *      своём знаменателе, на уровне не показывается.
 *   2. **Первый взгляд — ответ, а не данные.** На уровне сначала стоит то, что
 *      отвечает на вопрос «что сделано, а что нет», и не больше
 *      `DENSITY.headline` крупных чисел. Всё остальное уходит в раскрытие —
 *      не удаляется, а перемещается вглубь.
 *   3. **Спуск — один жест.** Нажатие на имя единицы уровня открывает её
 *      собственный уровень, сохраняя контекст анализа. Нажатие на сводную
 *      строку раскрывает подробность на месте. Возврат — по цепочке уровней
 *      в шапке. Три жеста, одинаковые везде.
 *
 * Модуль намеренно без разметки: правило легко нарушить рефакторингом и
 * невозможно заметить глазом, поэтому оно живёт в проверяемом коде, а не в
 * договорённости. Человеческая версия того же правила —
 * `docs/rules/screen-levels.md`.
 */

export type LevelId = "district" | "massif" | "actor" | "contour" | "work";

export interface LevelSpec {
  id: LevelId;
  /** Как называется одна единица уровня. */
  label: string;
  /** Склонения единицы: 1 / 2 / 5. */
  noun: [string, string, string];
  /**
   * В чём честно считается доля на этом уровне. Величина, у которой
   * знаменателя в этом списке нет, на уровне не показывается — она принадлежит
   * другому разговору.
   */
  denominators: readonly string[];
  parent: LevelId | null;
  child: LevelId | null;
  /** Что стоит в первом взгляде уровня. */
  headline: readonly string[];
  /** Что уходит в раскрытие: не удалено, а перемещено вглубь. */
  detail: readonly string[];
  /** Чего на уровне быть не должно — и почему именно. */
  refuse: readonly { what: string; why: string }[];
}

/**
 * Пять уровней. Их именно пять, и промежуточных нет: махалля — контекстный
 * слой карты, а не единица учёта (у неё нет ни контуров, ни обязательств),
 * а часть контура живёт внутри контура и своего экрана не заводит.
 */
export const LEVELS: readonly LevelSpec[] = [
  {
    id: "district",
    label: "Район",
    noun: ["район", "района", "районов"],
    denominators: [
      "массивы",
      "контуры",
      "хозяйства",
      "гектары района",
      // Ход работ район считает ОБЯЗАТЕЛЬСТВАМИ: обзор района уже
      // печатает ось «отчитались» над обязательствами открытых кампаний,
      // а отметки проверяющего — рядом, без доли. Пока знаменателя не было
      // в списке, правило 5 отклоняло честную величину, а экран её показывал.
      "обязательства района",
    ],
    parent: null,
    child: "massif",
    headline: [
      "срез и дата, на которую всё посчитано",
      "ось района «отчитались», отметки проверяющего рядом — либо названная причина, почему их нет",
      "шесть крупных чисел сезона: выполнено, после срока, текущая кампания, вода, карантин, поручения",
      "верхние ранги очереди внимания",
    ],
    detail: [
      "готовность данных по слоям",
      "структура землепользования",
      "структура культур",
      "обзорный кадр карты",
      "полное сравнение массивов",
      "нижние ранги очереди внимания",
      // Две точки одного сезона — четыре числа и две даты: в первом взгляде
      // они забрали бы его у ответа уровня. Но величина обещана ответом
      // `/season/picture`, и её отсутствие на экране читалось как «сравнивать
      // нечего» — утверждение, которого никто не делал.
      "сравнение с той же стадией прошлого сезона",
    ],
    refuse: [
      {
        what: "строки источника и отдельные замечания качества",
        why: "это единицы четвёртого и пятого уровня; в районном взгляде они не сравнимы между собой и вытесняют ответ",
      },
      {
        what: "сводный балл территории или хозяйства",
        why: "сложить покрытие геометрией с ходом кампании значит придумать показатель, которого в корпусе нет",
      },
    ],
  },
  {
    id: "massif",
    label: "Массив",
    noun: ["массив", "массива", "массивов"],
    denominators: [
      "контуры массива",
      "хозяйства массива",
      "гектары массива",
      // То же, что у района: ход работ на массиве считается его
      // обязательствами, а не контурами — один контур несёт работы
      // нескольких кампаний.
      "обязательства массива",
    ],
    parent: "district",
    child: "actor",
    headline: [
      "цепочка возврата в район",
      "ось массива «отчитались» по каждой кампании, отметки проверяющего рядом",
      "не больше четырёх крупных чисел: контуры, площадь, хозяйства, покрытие картой",
    ],
    detail: [
      "хозяйства массива с их отчётностью, по фактам со знаменателем (доля в срок, после срока)",
      "карантинные статусы массива",
      "переходы: контуры массива, проверка по массиву, карта массива",
    ],
    refuse: [
      {
        what: "рейтинг массивов «лидер / отстающий»",
        why: "у массива нет собственных работ и собственной ответственности: это разница между территориями, а не между хозяйствами. Хозяйства массива упорядочиваются по фактам со знаменателем (решение владельца 03.10.2026)",
      },
      {
        what: "доли от района",
        why: "знаменатель массива — сам массив; районная доля читается на уровне выше и здесь только сбивает",
      },
    ],
  },
  {
    id: "actor",
    label: "Хозяйство",
    noun: ["хозяйство", "хозяйства", "хозяйств"],
    denominators: [
      "контуры хозяйства",
      "обязательства хозяйства",
      "гектары хозяйства",
    ],
    parent: "massif",
    child: "contour",
    headline: [
      "цепочка возврата",
      "ось хозяйства «отчитались» по обязательствам, отметки проверяющего рядом",
      "земля хозяйства: контуры и гектары",
    ],
    detail: [
      "землепользования по контурам",
      "размещение культур по сезонам",
      "имена, алиасы и происхождение",
    ],
    refuse: [
      {
        what: "доля хозяйства в районе",
        why: "хозяйство не сравнивается с районом: у них разные знаменатели, и такая доля читается как оценка",
      },
      {
        what: "сводная оценка «хорошее / плохое хозяйство»",
        why: "такой величины в корпусе не существует, и очередь внимания не имеет права стать её чёрным ходом",
      },
    ],
  },
  {
    id: "contour",
    label: "Контур",
    noun: ["контур", "контура", "контуров"],
    denominators: ["работы на контуре", "части контура", "гектары контура"],
    parent: "actor",
    child: "work",
    headline: [
      "цепочка возврата, включая массив",
      "ось контура «отчитались»: что на нём заявлено, отметка проверяющего рядом",
      "кто пользуется землёй и сколько её",
    ],
    detail: [
      "площади по категориям",
      "части контура",
      "севооборот и размещение",
      "карантин контура",
      "замечания качества и происхождение",
    ],
    refuse: [
      {
        what: "районные и массивные доли",
        why: "контур — не доля территории, а единица учёта; проценты района на нём бессмысленны",
      },
    ],
  },
  {
    id: "work",
    label: "Работа",
    noun: ["работа", "работы", "работ"],
    denominators: ["контуры работы", "обязательства работы"],
    parent: "contour",
    child: null,
    headline: [
      "что за работа, чьё окно и истекло ли оно",
      "ось «отчитался исполнитель», отметка проверяющего рядом — раздельно",
    ],
    detail: [
      "контуры работы с отчётом и отметкой проверяющего по каждому",
      "кто внёс, от чьего имени, когда",
    ],
    refuse: [
      {
        what: "любая агрегация выше самой работы",
        why: "это лист: агрегаты принадлежат уровням выше и здесь были бы вторым источником тех же чисел",
      },
      {
        what: "«выполнено» без указания источника",
        why: "отчёт исполнителя принят как факт; отметка проверяющего — необязательная пометка поверх, а не альтернативное утверждение, которое можно слить с первым в одну полосу",
      },
    ],
  },
];

const BY_ID = new Map<LevelId, LevelSpec>(LEVELS.map((level) => [level.id, level]));

export function levelSpec(id: LevelId): LevelSpec {
  const found = BY_ID.get(id);
  if (!found) throw new Error(`Уровень не объявлен: ${id}`);
  return found;
}

/**
 * Цепочка уровней сверху вниз до указанного включительно.
 *
 * Ею рисуется возврат: человек, спустившийся до контура, обязан видеть, из
 * какого массива он пришёл, и вернуться туда одним нажатием, а не «назад» в
 * браузере, которое теряет разобранный контекст.
 */
export function levelChain(id: LevelId): LevelSpec[] {
  const chain: LevelSpec[] = [];
  let current: LevelId | null = id;
  while (current) {
    const spec = levelSpec(current);
    chain.unshift(spec);
    current = spec.parent;
  }
  return chain;
}

/**
 * Адрес уровня. `key === null` — список единиц уровня, а не одна единица.
 *
 * Работа адресуема наравне с остальными четырьмя: `/works/:id`. До этого её
 * адресом была карточка внутри очереди проверки, и это ограничение объявлялось
 * честно — сервер не отдавал работу ресурсом, и собственный адрес был бы
 * обещанием, которого некому выполнить. Теперь `/farm-obligations/{id}`
 * отвечает уровнем целиком (норматив, окно, ось «отчитались», контуры, авторство),
 * поэтому ссылка «эта работа» ведёт на саму работу, а не на список, в котором
 * её надо найти глазами.
 *
 * Список уровня работы остаётся очередью проверки: единого реестра всех работ
 * района нет и заводить его нельзя — это была бы районная агрегация под именем
 * пятого уровня.
 */
export function levelPath(id: LevelId, key: string | number | null = null): string {
  switch (id) {
    case "district":
      return "/";
    case "massif":
      return key === null ? "/" : `/massifs/${key}`;
    case "actor":
      return key === null ? "/actors" : `/actors/${key}`;
    case "contour":
      return key === null ? "/contours" : `/contours/${key}`;
    case "work":
      return key === null ? "/verification" : `/works/${key}`;
  }
}

/**
 * Бюджет плотности первого взгляда. Числа маленькие намеренно: экран, на
 * котором «важно всё», не расставляет приоритет — его расставляет читатель, и
 * каждый по-своему.
 */
export const DENSITY = {
  /** Крупных чисел в первом взгляде уровня (и в ответе ИИ — выгрузка ниже). */
  headline: 4,
  /**
   * Исключения по уровням. Район — шесть: «Обзор района» — единственный экран,
   * где хоким за один взгляд сверяет шесть разных осей сезона (работы, срок,
   * кампания, вода, карантин, поручения); четыре из них вытеснили бы две
   * оси в раскрытие, и экран перестал бы отвечать «как дела в районе»
   * (план v2 §1, решение владельца 07.10.2026). Ответ ИИ этим исключением не
   * пользуется: в выгрузку уходит общий `headline`.
   */
  headlineByLevel: { district: 6 } as Partial<Record<LevelId, number>>,
  /** Пунктов списка до раскрытия. */
  preview: 5,
  /** Строк таблицы до раскрытия. */
  tablePreview: 8,
} as const;

/** Бюджет крупных чисел первого взгляда для уровня экрана. */
export function headlineBudget(level: LevelId): number {
  return DENSITY.headlineByLevel[level] ?? DENSITY.headline;
}

/**
 * Обрезать список до бюджета первого взгляда.
 *
 * Возвращает и остаток: скрытое обязано быть названо числом, иначе раскрытие
 * выглядит как украшение, а не как «здесь ещё N строк».
 */
export function preview<T>(
  rows: ReadonlyArray<T>,
  limit: number,
): { shown: T[]; hidden: number } {
  return {
    shown: rows.slice(0, limit),
    hidden: Math.max(0, rows.length - limit),
  };
}

/**
 * Выгрузка уровня для сервера: подпись и знаменатели, плюс бюджеты плотности.
 *
 * Сервер обязан считать долю в тех же знаменателях и складывать первый взгляд
 * по тем же бюджетам, что и экран, — иначе правило 5 пропустит величину,
 * которой на уровне нет места, а переполнение уйдёт вглубь на сервере и
 * останется на экране у человека. Раньше это держалось повтором констант в
 * Python; теперь клиент отдаёт их сам, а повтор остался только запасным
 * значением на случай, если выгрузки нет.
 *
 * Ключ `table_preview` записан по-питоновски намеренно: имена полей выгрузки —
 * это контракт с сервером, и переименование на границе пришлось бы держать
 * третьим местом, которое можно забыть.
 */
export function levelsExport(): {
  levels: Record<string, { caption: string; denominators: string[] }>;
  density: { headline: number; preview: number; table_preview: number };
} {
  return {
    levels: Object.fromEntries(
      LEVELS.map((level) => [
        level.id,
        { caption: level.label, denominators: [...level.denominators] },
      ]),
    ),
    density: {
      headline: DENSITY.headline,
      preview: DENSITY.preview,
      table_preview: DENSITY.tablePreview,
    },
  };
}
